import Req from '@/services/Request';

const MAX_LOCAL_SIZE = 2 * 1024 * 1024;
const MAX_AGE_MS = 4 * 60 * 60 * 1000;
const META_KEY = '_localDriverMeta';
const ENABLE_INDEXED_DB = true; // IndexedDB功能开关
const ENABLE_Server_DB = false ; // IndexedDB功能开关

function isLocalStorageWorking(): boolean {
  try {
    const testKey = '__test_key__';
    localStorage.setItem(testKey, '1');
    const ok = localStorage.getItem(testKey) === '1';
    localStorage.removeItem(testKey);
    return ok;
  } catch {
    return false;
  }
}

let localAvailable = isLocalStorageWorking();

const dbName = 'localDriverDB';
const storeName = 'keyval';

let db: IDBDatabase | null = null;

function waitForTransactionComplete(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error('IDB transaction failed'));
    tx.onabort = () => reject(tx.error ?? new Error('IDB transaction aborted'));
  });
}

function openDB(): Promise<IDBDatabase> {
  if (db) return Promise.resolve(db);

  return new Promise((resolve, reject) => {
    const req = indexedDB.open(dbName, 1);
    req.onupgradeneeded = () => {
      req.result.createObjectStore(storeName);
    };
    req.onsuccess = () => {
      db = req.result;
      db.onclose = () => (db = null);
      resolve(db);
    };
    req.onerror = () => {
      db = null;
      reject(req.error);
    };
  });
}

function toPureJSON<T>(obj: T): T {
  try {
    return JSON.parse(JSON.stringify(obj));
  } catch {
    return obj;
  }
}

async function idbRemoveExpiredItems(retried = false): Promise<void | false> {
  try {
    const dbInstance = await openDB();
    const tx = dbInstance.transaction(storeName, 'readwrite');
    const store = tx.objectStore(storeName);
    const now = Date.now();

    return new Promise((resolve, reject) => {
      const req = store.openCursor();

      req.onsuccess = (e) => {
        const cursor = (e.target as IDBRequest<IDBCursorWithValue>).result;

        if (cursor) {
          const { ts } = cursor.value;

          if (now - ts > MAX_AGE_MS) {
            try {
              const deleteReq = cursor.delete();
              deleteReq.onsuccess = () => {
                try {
                  cursor.continue();
                } catch (err) {
                  reject(err);
                }
              };
              deleteReq.onerror = () => {
                try {
                  cursor.continue();
                } catch (err) {
                  reject(err);
                }
              };
            } catch (err) {
              try {
                cursor.continue();
              } catch (err2) {
                reject(err2);
              }
            }
          } else {
            try {
              cursor.continue();
            } catch (err) {
              reject(err);
            }
          }
        } else {
          resolve();
        }
      };

      req.onerror = () => reject(req.error);

      tx.oncomplete = () => {};
      tx.onerror = () => {};
      tx.onabort = () => {};
    });
  } catch (e: any) {
    if (!retried) {
      db = null;
      return idbRemoveExpiredItems(true);
    }
    return false;
  }
}

async function idbSetItem(key: string, value: any, retried = false): Promise<void | false> {
  try {
    const dbInstance = await openDB();
    await idbRemoveExpiredItems();
    const tx = dbInstance.transaction(storeName, 'readwrite');
    const store = tx.objectStore(storeName);
    const safeValue = toPureJSON(value);
    store.put({ value: safeValue, ts: Date.now() }, key);
    await waitForTransactionComplete(tx);
  } catch (e: any) {
    if (!retried) {
      db = null;
      return idbSetItem(key, value, true);
    }
    return false;
  }
}

async function idbGetItem(key: string, retried = false): Promise<any | null | false> {
  try {
    const dbInstance = await openDB();
    const tx = dbInstance.transaction(storeName, 'readonly');
    const store = tx.objectStore(storeName);

    return new Promise((resolve, reject) => {
      const req = store.get(key);
      req.onsuccess = () => {
        const result = req.result;
        if (!result) return resolve(null);
        if (Date.now() - result.ts > MAX_AGE_MS) {
          idbRemoveItem(key);
          return resolve(null);
        }
        resolve(result.value);
      };
      req.onerror = () => reject(req.error ?? new Error('IndexedDB get request failed'));
    });
  } catch (e: any) {
    if (!retried) {
      db = null;
      return idbGetItem(key, true);
    }
    return null;
  }
}

async function idbRemoveItem(key: string, retried = false): Promise<void | false> {
  try {
    const dbInstance = await openDB();
    const tx = dbInstance.transaction(storeName, 'readwrite');
    tx.objectStore(storeName).delete(key);
    await waitForTransactionComplete(tx);
  } catch (e: any) {
    if (!retried) {
      db = null;
      return idbRemoveItem(key, true);
    }
    return false;
  }
}

async function idbGetAllKeys(retried = false): Promise<string[] | false> {
  try {
    const dbInstance = await openDB();
    const tx = dbInstance.transaction(storeName, 'readonly');
    const store = tx.objectStore(storeName);

    return await new Promise((resolve, reject) => {
      const keys: string[] = [];
      const req = store.openCursor();
      req.onsuccess = (e) => {
        const cursor = (e.target as IDBRequest).result;
        if (cursor) {
          if (Date.now() - cursor.value.ts <= MAX_AGE_MS) {
            keys.push(cursor.key as string);
          }
          cursor.continue();
        } else {
          resolve(keys);
        }
      };
      req.onerror = () => reject(req.error ?? new Error('IDB cursor request failed'));
    });
  } catch (e: any) {
    if (!retried) {
      db = null;
      return idbGetAllKeys(true);
    }
    return false;
  }
}

async function idbClear(retried = false): Promise<void | false> {
  try {
    const dbInstance = await openDB();
    const tx = dbInstance.transaction(storeName, 'readwrite');
    tx.objectStore(storeName).clear();
    await waitForTransactionComplete(tx);
  } catch (e: any) {
    if (!retried) {
      db = null;
      return idbClear(true);
    }
    return false;
  }
}

function getMeta(): Record<string, number> {
  try {
    return JSON.parse(localStorage.getItem(META_KEY) || '{}');
  } catch {
    return {};
  }
}

function saveMeta(meta: Record<string, number>) {
  try {
    localStorage.setItem(META_KEY, JSON.stringify(meta));
  } catch {
    localAvailable = false;
  }
}

function cleanExpired(meta: Record<string, number>) {
  const now = Date.now();
  for (const key in meta) {
    if (now - meta[key] > MAX_AGE_MS) {
      try {
        localStorage.removeItem(key);
      } catch {
        localAvailable = false;
      }
      delete meta[key];
    }
  }
}

function getLocalStorageSize(): number {
  let total = 0;
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k) continue;
      const v = localStorage.getItem(k);
      total += k.length + (v ? v.length : 0);
    }
  } catch {
    localAvailable = false;
  }
  return total;
}

function trimToFit(meta: Record<string, number>) {
  let size = getLocalStorageSize();
  if (size <= MAX_LOCAL_SIZE) return;

  const targetSize = MAX_LOCAL_SIZE / 2;
  const sorted = Object.entries(meta).sort((a, b) => a[1] - b[1]);
  for (const [key] of sorted) {
    try {
      const val = localStorage.getItem(key);
      localStorage.removeItem(key);
      delete meta[key];
      size -= key.length + (val ? val.length : 0);
      if (size <= targetSize) break;
    } catch {
      localAvailable = false;
      break;
    }
  }
}

export const localDriver = {
  async setItem(key: string, value: any): Promise<void | false> {
    if (!key) return;
    const ts = Date.now();
    if (localAvailable) {
      try {
        const meta = getMeta();
        meta[key] = ts;
        localStorage.setItem(key, JSON.stringify(value));
        cleanExpired(meta);
        trimToFit(meta);
        saveMeta(meta);
      } catch {
        localAvailable = false;
      }
    }
    if(ENABLE_INDEXED_DB)
    {
      await idbSetItem(key, value);
    }
    if(ENABLE_Server_DB)
    {
      return new Promise((resolve, reject) => 
        {
            new Req().webPost(
              { key:key,val:value },
              (res) => {
                if (res && res.success) resolve();
                else reject(false);
              },
              '/common/storage/set',
              (err) => reject(false)
            );
        });
    }
    return null
  },

  async getItem(key: string): Promise<any | null | false> {
    if (!key) return null;
    if (localAvailable) {
      try {
        const meta = getMeta();
        const ts = meta[key];
        if (!ts || Date.now() - ts > MAX_AGE_MS) {
          localStorage.removeItem(key);
          delete meta[key];
          saveMeta(meta);
        } else {
          const val = localStorage.getItem(key);
          if (val !== null)
          {
            return JSON.parse(val);
          }
        }
      } catch {
        localAvailable = false;
      }
    }

    if(ENABLE_INDEXED_DB)
    {
      try {
        const backup = await idbGetItem(key);
        if (backup !== false && backup !== null && !(typeof backup === 'object' && Object.keys(backup).length === 0))
        {
          return backup;
        }
      } catch {
        // IndexedDB failure is non-fatal; fall through to return null
      }
    }
    
    if(ENABLE_Server_DB){
      return new Promise((resolve, reject) => {
        new Req().webPost(
          { key:key },
          (res) => {
             if (res && res.data) {
               resolve(res.data);
             } else {
               resolve(null);
             }
          },
          '/common/storage/get',
          (err) => reject(null)
        );
      });
    }
    else{
      return null;
    }
  },

  async removeItem(key: string): Promise<void | false> {
    if (localAvailable) {
      try {
        const meta = getMeta();
        delete meta[key];
        localStorage.removeItem(key);
        saveMeta(meta);
      } catch {
        localAvailable = false;
      }
    }
    return await idbRemoveItem(key);
  },

  async getAllKeys(): Promise<string[] | false> {
    if (localAvailable) {
      try {
        const meta = getMeta();
        const now = Date.now();
        return Object.entries(meta)
          .filter(([_, ts]) => now - ts <= MAX_AGE_MS)
          .map(([key]) => key);
      } catch {
        localAvailable = false;
      }
    }
    return await idbGetAllKeys();
  },

  async clear(): Promise<void | false> {
    if (localAvailable) {
      try {
        localStorage.clear();
      } catch {
        localAvailable = false;
      }
    }
    return await idbClear();
  },
};

(window as any).localDriver = localDriver